// @flow
import type { Element, Node } from 'react';
import React, { createContext, useState } from 'react';
import ApiResponse from '../model/ApiResponse';
import User from '../model/User';
import { doDelete, doGet, doPost, doPostMultipart } from '../util/api';

type AuthProps = {
  isAuthenticated: boolean,
  user: ?User,
  setToken: (?string) => Promise<void>,
  setUser: (User) => void,
  doGet: (url: string) => Promise<ApiResponse>,
  doPost: (url: string, data: any, files: ?any) => Promise<ApiResponse>,
  doDelete: (url: string) => Promise<ApiResponse>,
};

export const AuthContext = createContext<AuthProps>({});

const tokenKey = 'app_auth_token';
const userKey = 'app_auth_user';

export const getAuthToken = (): ?string => localStorage.getItem(tokenKey);

export const setAuthToken = (token: ?string) => {
  if (!!token) {
    localStorage.setItem(tokenKey, token);
    fetchUser().then();
  } else {
    localStorage.removeItem(tokenKey);
  }
};

export const getUser = (): ?User => User.parse(localStorage.getItem(userKey));

const fetchUser = async (): Promise<?User> => {
  if (!!getAuthToken()) {
    const response: ApiResponse = await doGet('/customer/me');
    if (response.ok && response.data) {
      localStorage.setItem(userKey, JSON.stringify(response.data));
      return User.parse(response.data);
    }
  }
  return getUser();
};

type ProviderProps = {
  children: Node[] | Element<any>,
};

export const AuthProvider = (props: ProviderProps) => {
  const { children } = props;

  const [token, setToken] = useState<?string>(getAuthToken);
  const [user, setUser] = useState<?User>(getUser);

  const updateToken = async (token): Promise<void> => {
    if (token) {
      localStorage.setItem(tokenKey, token);
      const user: ?User = await fetchUser();
      if (!!user) {
        setUser(user);
        setToken(token);
      }
    } else {
      localStorage.removeItem(tokenKey);
      localStorage.removeItem(userKey);
      setUser(null);
      setToken(null);
    }
  };

  const updateUser = (user: User) => {
    if (!!user) {
      setUser(user);
      localStorage.setItem(userKey, JSON.stringify(user));
    }
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated: !!token,
      user: user,
      setToken: updateToken,
      setUser: updateUser,
      doGet: async (url: string): Promise<ApiResponse> => {
        const response: ApiResponse = await doGet(url);
        if (response.is401Error()) {
          await updateToken(null);
        }
        return response;
      },
      doPost: async (url: string, data: any, files: ?any): Promise<ApiResponse> => {
        let response: ApiResponse;
        if (!!files) {
          response = await doPostMultipart(url, data, files);
        } else {
          response = await doPost(url, data);
        }
        if (response.is401Error()) {
          await updateToken(null);
        }
        return response;
      },
      doDelete: async (url: string): Promise<ApiResponse> => {
        const response: ApiResponse = await doDelete(url);
        if (response.is401Error()) {
          await updateToken(null);
        }
        return response;
      },
    }}>
      {children}
    </AuthContext.Provider>
  );
};

type ConsumerProps = {
  children: (any) => Node,
};

export const AuthConsumer = (props: ConsumerProps) => {
  const { children } = props;

  return (
    <AuthContext.Consumer>
      {children}
    </AuthContext.Consumer>
  );
};
